import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft,
  Music,
  Bell,
  Plus,
  Edit,
  Trash2,
  Mail,
  Globe,
  UserPlus,
  Shield,
  FileText,
  AlertCircle,
  CheckCircle,
  Users,
  Upload,
  Sparkles,
  Crown,
  StickyNote,
  LayoutGrid,
  Repeat,
  Send,
  Inbox,
  Download,
  Trophy,
  Calendar,
  MessageSquare,
} from "lucide-react";

const AdminGuide = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: "Creating Events",
      icon: <Plus className="w-5 h-5" />,
      color: "text-green-400",
      steps: [
        "Go to Events → 'Add Event'",
        "Fill in Title, Type (Rehearsal/Concert), Location, Date & times",
        "Tick 'TBC' next to any time that isn't confirmed yet",
        "Set Status: Confirmed, Provisional, or Cancelled",
        "Optional: enable reminders (24h or 48h before the event)",
        "Optional: set it up as recurring — creates the full run of events in one click",
        "Click 'Create Event'"
      ]
    },
    {
      title: "Editing an Event",
      icon: <Edit className="w-5 h-5" />,
      color: "text-blue-400",
      badge: "New",
      steps: [
        "Open the event details page",
        "Click the 'Edit Event' button in the header (admin-only)",
        "Change title, times, status, location or description in the dialog",
        "Hit Save — the page reloads with the new info and reminders remain linked"
      ]
    },
    {
      title: "Admin-only Notes on Events",
      icon: <StickyNote className="w-5 h-5" />,
      color: "text-amber-400",
      badge: "New",
      steps: [
        "Open the event details page",
        "Look for the amber 'Admin Notes' card (shows 'Admins only' badge)",
        "Type anything your fellow admins need to know — parking, fee splits, comp tickets, wardrobe notes",
        "Click Save. Players never see this card",
        "The note appears for every admin in your band on this event"
      ]
    },
    {
      title: "Deleting an Event",
      icon: <Trash2 className="w-5 h-5" />,
      color: "text-red-400",
      badge: "New",
      steps: [
        "From Events list: click the trash icon on the event card",
        "Or from Event detail: click 'Delete Event' in the header",
        "Confirm — the event, its attendance, music programme and uploaded files are permanently removed",
        "Players will no longer see it in their schedule"
      ]
    },
    {
      title: "Managing Event Status",
      icon: <AlertCircle className="w-5 h-5" />,
      color: "text-yellow-400",
      steps: [
        "Provisional — use when the event isn't locked in yet",
        "Confirmed — event is definitely happening",
        "Cancelled — event is off; stays visible with a strikethrough so players see it was called off",
        "Change status anytime via Edit Event"
      ]
    },
    {
      title: "Adding Players",
      icon: <UserPlus className="w-5 h-5" />,
      color: "text-blue-400",
      steps: [
        "Go to Players → 'Add Player'",
        "Enter name and email",
        "Assign one or more sections (players can cover multiple)",
        "Optional: set player type (Member / Dep / Inactive / Info Only)",
        "A welcome email with login details is sent automatically"
      ]
    },
    {
      title: "Signup via Band Code (behaviour)",
      icon: <Sparkles className="w-5 h-5" />,
      color: "text-amber-400",
      badge: "New",
      steps: [
        "When players sign up with your band code, they now MUST pick their section",
        "Share your code from Settings → 'Band Code' (click the copy icon)",
        "On the signup form they enter the code, the app auto-detects your band, then shows a required 'Your Section' dropdown",
        "They appear in your Players list already assigned to that section",
        "You can still change their section from the Players page later"
      ]
    },
    {
      title: "Managing Sections",
      icon: <Music className="w-5 h-5" />,
      color: "text-purple-400",
      steps: [
        "Go to Sections from the sidebar",
        "Use +/- buttons to add or remove sections",
        "Arrow buttons reorder sections; click 'Save Order' to commit",
        "Click a section to see assigned players",
        "Sections with assigned players can't be deleted until the players are moved or removed"
      ]
    },
    {
      title: "Uploading Event Files",
      icon: <FileText className="w-5 h-5" />,
      color: "text-orange-400",
      steps: [
        "Open an event's details page",
        "Click the 'Files' tab",
        "Click 'Upload File' (max 50MB, any file type)",
        "Use for sheet music, programmes, seating plans, stage plots",
        "Files are immediately available for all players to download"
      ]
    },
    {
      title: "Sending Reminders & Nudges",
      icon: <Bell className="w-5 h-5" />,
      color: "text-pink-400",
      steps: [
        "Open the event details page → 'Admin Tools' section",
        "'Send Reminder to Attending' — emails + pushes confirmed attendees",
        "'Send Reminder to Non-Responders' (Nudge) — chases everyone who hasn't said Yes or No yet, including anyone who replied 'Maybe' (a Maybe still leaves you unable to plan cover). Each person only gets nudged once per event",
        "For always-on reminders: tick the reminder option when creating the event (24h or 48h before)"
      ]
    },
    {
      title: "Committee Member Flag",
      icon: <Crown className="w-5 h-5" />,
      color: "text-amber-400",
      badge: "New",
      steps: [
        "Go to Settings → Manage Member Roles",
        "Every member row has a 'Committee' toggle next to the admin toggle",
        "Flip it on for treasurer, chairperson, secretary, MD, etc.",
        "The number of committee members is shown at the top of the Committee Email card",
        "Committee members are the ones who will receive emails sent to your committee inbound address (see next card)"
      ]
    },
    {
      title: "Committee Email Address",
      icon: <Inbox className="w-5 h-5" />,
      color: "text-amber-400",
      badge: "New",
      steps: [
        "Go to Settings → 'Email Identity' card (admin-only)",
        "Your auto-generated committee address looks like committee.<yourband>@vivomusicianmanagement.co.uk",
        "Anyone marked Committee receives every email sent to that address",
        "Replies from recipients go straight back to the original sender",
        "Use the 'Rename slug' button to change the local part — uppercase letters allowed for nicer display"
      ]
    },
    {
      title: "Sending Band-wide Notifications",
      icon: <Send className="w-5 h-5" />,
      color: "text-indigo-400",
      steps: [
        "Go to Notifications from the sidebar (admin-only)",
        "Type title + message, choose type (general/event/reminder)",
        "Click Send — goes out as email + push to every band member",
        "Notifications are listed under 'Sent History'",
        "Delete individual notifications with the trash icon, or clear the whole history with 'Clear all'"
      ]
    },
    {
      title: "Inviting New Members",
      icon: <Users className="w-5 h-5" />,
      color: "text-cyan-400",
      steps: [
        "Go to Settings → 'Band Code'",
        "Share the 6-character code with new musicians",
        "They sign up, enter the code, pick their section and they're in",
        "Regenerate the code anytime if it's been shared too widely"
      ]
    },
    {
      title: "Admin Hub",
      icon: <LayoutGrid className="w-5 h-5" />,
      color: "text-blue-400",
      badge: "New",
      steps: [
        "Click 'Admin' in the sidebar (only visible to admins)",
        "Landing page with tiles for Notifications, Reports, Members & Roles, Sections, Bulk Imports, Band Settings, Monthly Digest",
        "Use this as a one-stop launchpad for everything admin-y"
      ]
    },
    {
      title: "Bulk Imports (CSV)",
      icon: <Upload className="w-5 h-5" />,
      color: "text-emerald-400",
      steps: [
        "Go to Admin → Bulk Imports (or the Import button on each page)",
        "Three importers: Players, Events, Music",
        "Click 'Download Template' to get a pre-filled CSV with the exact columns",
        "Fill it in using a spreadsheet app, upload — you get a preview to confirm before anything is saved",
        "Errors are shown per-row with the exact reason so you can fix and retry"
      ]
    },
    {
      title: "Email Digest Settings",
      icon: <Mail className="w-5 h-5" />,
      color: "text-red-400",
      steps: [
        "Go to Settings → 'Email Notifications'",
        "Daily Admin Digest — toggle on/off. When on, every admin in the band gets a 4pm UTC email with upcoming events + who hasn't RSVP'd",
        "Monthly Member Digest — toggle on/off. Sent to every band member on the 1st of each month at 09:00 UTC. Covers: new events since the last digest, events this month, events coming up in the next 2-4 months (concerts only, plus cancelled rehearsals)",
        "Click 'Send monthly digest now' to fire it manually — useful for test runs or catching up after disabling"
      ]
    },
    {
      title: "Promoting to Admin",
      icon: <Shield className="w-5 h-5" />,
      color: "text-amber-400",
      steps: [
        "Settings → Manage Member Roles",
        "Find the member and click 'Make Admin' (or 'Remove Admin' to demote)",
        "You can't demote yourself or the last admin in the band",
        "New admins immediately see the Admin section in the sidebar and can manage everything"
      ]
    },
    {
      title: "Public Concert Widget",
      icon: <Globe className="w-5 h-5" />,
      color: "text-indigo-400",
      steps: [
        "Settings → 'Public Concert Widget'",
        "Click 'Generate Embed Code'",
        "Copy the iframe HTML into your band's website (Wix, Squarespace, WordPress, plain HTML — all fine)",
        "Shows upcoming confirmed + provisional concerts; cancelled events are hidden automatically",
        "Regenerate the token anytime to revoke an old embed"
      ]
    },
    {
      title: "Running Multiple Bands",
      icon: <Repeat className="w-5 h-5" />,
      color: "text-fuchsia-400",
      badge: "New",
      steps: [
        "You can belong to (and admin) several bands simultaneously",
        "Sidebar band switcher shows every band you're in with your role per band",
        "Click 'Join another band' in the switcher and paste the other band's code",
        "Each band is fully data-isolated — events, players, attendance, music, settings",
        "Use All-Bands Calendar (/calendar/all) to view everything in one colour-coded monthly view"
      ]
    },
    // iter-104 → iter-106 — new admin tooling.
    {
      title: "Uniform Inventory Reports",
      icon: <Download className="w-5 h-5" />,
      color: "text-cyan-300",
      badge: "New",
      steps: [
        "Go to Inventory from the sidebar",
        "Click 'Uniform PDF' to generate a uniform-only report grouped by player (sizes, condition, asset tags)",
        "Click 'Uniform Excel' for the same data in a sortable spreadsheet",
        "Unassigned uniforms appear in a separate 'Store Room' section at the bottom",
        "Use the 'Insurance Report' button alongside for an insurance-ready schedule (excludes stolen / sold items)"
      ]
    },
    {
      title: "Public Concert Embed Widget",
      icon: <Globe className="w-5 h-5" />,
      color: "text-blue-300",
      badge: "Updated",
      steps: [
        "Go to Band Settings → 'Concert Widget' to copy your embed URL and iframe snippet",
        "Paste it onto your band's public website to show upcoming confirmed concerts",
        "Times now display in UK local time automatically (handles GMT ↔ BST switch)",
        "Provisional events are NEVER shown publicly — only confirmed concerts appear",
        "Private events show a date-only placeholder (title and location are redacted)"
      ]
    },
    {
      title: "Persistent Event Detail View",
      icon: <Calendar className="w-5 h-5" />,
      color: "text-emerald-300",
      badge: "New",
      steps: [
        "Open any event and the tab you select (Members / Deps / Repertoire / Costs) is remembered per-event",
        "Reload the page or come back later — the same tab opens automatically",
        "Useful for treasurers who live in Costs and section leaders who stay in Members"
      ]
    },
    {
      title: "Achievements & Player Engagement",
      icon: <Trophy className="w-5 h-5" />,
      color: "text-amber-300",
      badge: "Updated",
      steps: [
        "Players earn attendance badges automatically — no admin work required",
        "Newest tiers: Section Stalwart (50 events), Two-Year Club (24 consecutive months)",
        "When a player unlocks a new badge they see a celebration toast on the Dashboard",
        "Encourage RSVPs by reminding members about the Fast Responder badge (RSVP within 24h)"
      ]
    },
    {
      title: "Chat Rooms — Visibility Rules",
      icon: <MessageSquare className="w-5 h-5" />,
      color: "text-purple-300",
      badge: "Updated",
      steps: [
        "Every band has a General room everyone sees",
        "Section rooms auto-generate from your Sections list — players only see rooms for sections they're in",
        "Family rooms (e.g. 'All Cornets') group related sections — players in any covered section see them",
        "Musical Directors get visibility into every room — useful for cross-band announcements",
        "Use Settings → 'Custom rooms' to create invite-only rooms (e.g. Committee, Quartet)",
        "Player unread badges now match the per-room counts exactly — sidebar and rooms list stay in sync"
      ]
    }
  ];

  const quickTips = [
    "Encourage new members to sign up via Band Code — they'll be correctly sectioned from day one",
    "Tag a trusted member as Committee before sharing the committee email address externally",
    "Use Provisional status for gigs that might change — players know not to commit firmly",
    "Nudge non-responders 3 days before an important event",
    "The Admin Notes card is perfect for things like 'bring extra music stands' or 'park on Main St, not the church lot'",
    "Upload sheet music to events so players can prepare beforehand"
  ];

  return (
    <div className="space-y-6" data-testid="admin-guide">
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            Admin Guide
          </h1>
          <p className="text-muted-foreground mt-1">
            Everything you need to manage your band effectively
          </p>
        </div>
      </div>

      <Card className="glass-card border-primary/20">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <strong className="text-foreground">What&apos;s new:</strong> mandatory section on band-code signup, a Committee Member flag with an automatic <em>committee.&lt;band&gt;@…</em> inbound email address, admin-only notes on every event, Edit Event from the detail page, Nudge Non-Responders, multi-band admin with switcher, Monthly Member Digest, and a unified Admin Hub at <code>/admin</code>.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <Card className="glass-card border-primary/20">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2 text-primary">
            <CheckCircle className="w-5 h-5" />
            Quick Tips
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {quickTips.map((tip, index) => (
              <li key={index} className="flex gap-2 text-sm text-muted-foreground">
                <span className="text-primary">•</span>
                {tip}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {sections.map((section, index) => (
          <Card key={index} className="glass-card" data-testid={`admin-guide-card-${index}`}>
            <CardHeader className="pb-3">
              <CardTitle className={`font-heading text-lg flex items-center gap-2 ${section.color}`}>
                {section.icon}
                {section.title}
                {section.badge && (
                  <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/15 text-primary">
                    {section.badge}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-2">
                {section.steps.map((step, stepIndex) => (
                  <li key={stepIndex} className="flex gap-3 text-sm">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center font-bold">
                      {stepIndex + 1}
                    </span>
                    <span className="text-muted-foreground">{step}</span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminGuide;
